import Header from "@/components/Header";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useEventDetail } from "@/hooks/useEvents";
import { useEventParticipants, useRSVP, useUpdateRSVP, useLeaveEvent, useRemoveParticipant } from "@/hooks/useEventParticipants";
import { useRouteById } from "@/hooks/useRoutes";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, MapPin, Users, Clock, ArrowLeft, Edit, Route as RouteIcon, X, CheckCircle2, Circle, Home } from "lucide-react";
import { format } from "date-fns";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";
import { CancelEventDialog } from "@/components/CancelEventDialog";
import { EventComments } from "@/components/EventComments";
import EventPhotoGallery from "@/components/EventPhotoGallery";
import { WaiverDialog } from "@/components/WaiverDialog";
import { useState } from "react";

const EventDetail = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: event, isLoading: eventLoading } = useEventDetail(eventId);
  const { data: participants, isLoading: participantsLoading } = useEventParticipants(eventId);
  const { route } = useRouteById(event?.route_id);
  const rsvpMutation = useRSVP();
  const updateRsvpMutation = useUpdateRSVP();
  const leaveEventMutation = useLeaveEvent();
  const removeParticipantMutation = useRemoveParticipant();

  const [showWaiverDialog, setShowWaiverDialog] = useState(false);
  const [pendingRsvpStatus, setPendingRsvpStatus] = useState<'going' | 'maybe' | null>(null);

  const userParticipation = participants?.find(p => p.user_id === user?.id);
  const isEventCreator = user && event && event.created_by === user.id;
  const goingCount = participants?.filter(p => p.status === 'going').length || 0;
  const maybeCount = participants?.filter(p => p.status === 'maybe').length || 0;

  const handleRSVP = (status: 'going' | 'maybe') => {
    if (!eventId) return;

    // If waiver required and user hasn't participated yet, show waiver dialog
    if (event?.waiver_required && !userParticipation && (status === 'going' || status === 'maybe')) {
      setPendingRsvpStatus(status);
      setShowWaiverDialog(true);
      return;
    }

    if (userParticipation) {
      updateRsvpMutation.mutate({ eventId, status });
    } else {
      rsvpMutation.mutate({ eventId, status, waiverSigned: false });
    }
  };

  const handleWaiverAccept = (waiverData: any) => {
    if (!eventId || !pendingRsvpStatus) return;
    
    rsvpMutation.mutate(
      { eventId, status: pendingRsvpStatus, waiverSigned: true, waiverData },
      {
        onSuccess: () => {
          setShowWaiverDialog(false);
          setPendingRsvpStatus(null);
        },
      }
    );
  };

  const handleLeave = () => {
    if (!eventId) return;
    leaveEventMutation.mutate(eventId);
  };

  const handleRemoveParticipant = (userId: string) => {
    if (!eventId) return;
    removeParticipantMutation.mutate({ eventId, userId });
  };

  if (eventLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <Skeleton className="h-96 w-full" />
        </main>
      </div>
    );
  }

  if (!event) {
    const redirectPath = user ? '/events' : '/';
    const buttonText = user ? 'Back to Events' : 'Return to Home';
    const ButtonIcon = user ? ArrowLeft : Home;

    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center py-16">
            <Calendar className="h-16 w-16 mx-auto mb-6 text-muted-foreground" />
            <h1 className="text-2xl font-bold text-foreground mb-4">Event Not Found</h1>
            <p className="text-muted-foreground mb-6">
              This event may have been cancelled or doesn't exist.
            </p>
            <Button onClick={() => navigate(redirectPath)}>
              <ButtonIcon className="h-4 w-4 mr-2" />
              {buttonText}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-4">
          <Button variant="ghost" onClick={() => navigate('/events')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Events
          </Button>
          {isEventCreator && event.status === 'upcoming' && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate(`/events/${eventId}/edit`)}>
                <Edit className="w-4 h-4 mr-2" />
                Edit Event
              </Button>
              <CancelEventDialog eventId={eventId!} eventTitle={event.title} />
            </div>
          )}
        </div>

        {event.status === 'cancelled' && (
          <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-lg mb-4">
            <p className="font-semibold">This event has been cancelled</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-3xl mb-2">{event.title}</CardTitle>
                    <div className="flex gap-2 mt-4">
                      <Badge>{event.event_type.replace('_', ' ')}</Badge>
                      {event.difficulty_estimate && (
                        <Badge variant="outline">{event.difficulty_estimate}</Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center text-foreground">
                  <Calendar className="w-5 h-5 mr-3" />
                  <div>
                    <p className="font-medium">Start Time</p>
                    <p className="text-muted-foreground">{format(new Date(event.start_date), 'PPP p')}</p>
                  </div>
                </div>

                {event.meeting_point_description && (
                  <div className="flex items-center text-foreground">
                    <MapPin className="w-5 h-5 mr-3" />
                    <div>
                      <p className="font-medium">Meeting Point</p>
                      <p className="text-muted-foreground">{event.meeting_point_description}</p>
                    </div>
                  </div>
                )}

                {event.estimated_duration_hours && (
                  <div className="flex items-center text-foreground">
                    <Clock className="w-5 h-5 mr-3" />
                    <div>
                      <p className="font-medium">Duration</p>
                      <p className="text-muted-foreground">{event.estimated_duration_hours} hours</p>
                    </div>
                  </div>
                )}

                {route && (
                  <div className="flex items-center text-foreground">
                    <RouteIcon className="w-5 h-5 mr-3" />
                    <div>
                      <p className="font-medium">Route</p>
                      <Link 
                        to={`/route/${route.id}`}
                        className="text-primary hover:underline"
                      >
                        {route.name} {route.length_miles && `(${route.length_miles.toFixed(1)} mi)`}
                      </Link>
                    </div>
                  </div>
                )}

                {event.description && (
                  <div className="pt-4 border-t">
                    <h3 className="font-semibold text-lg mb-2">Description</h3>
                    <p className="text-muted-foreground whitespace-pre-wrap">{event.description}</p>
                  </div>
                )}

                {event.equipment_requirements && event.equipment_requirements.length > 0 && (
                  <div className="pt-4 border-t">
                    <h3 className="font-semibold text-lg mb-2">Equipment Requirements</h3>
                    <div className="flex flex-wrap gap-2">
                      {event.equipment_requirements.map((item, i) => (
                        <Badge key={i} variant="secondary">{item}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {event.notes && (
                  <div className="pt-4 border-t">
                    <h3 className="font-semibold text-lg mb-2">Additional Notes</h3>
                    <p className="text-muted-foreground">{event.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Photo Gallery */}
            {event.photo_urls && event.photo_urls.length > 0 && (
              <EventPhotoGallery photos={event.photo_urls} eventTitle={event.title} />
            )}
          </div>

          <div className="space-y-6">
            {user && event.status === 'upcoming' && (
              <Card>
                <CardHeader>
                  <CardTitle>RSVP</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {userParticipation ? (
                    <>
                      <p className="text-sm text-muted-foreground mb-3">
                        Your status: <Badge>{userParticipation.status}</Badge>
                      </p>
                      <Button 
                        variant="outline" 
                        className="w-full" 
                        onClick={() => handleRSVP('going')}
                        disabled={userParticipation.status === 'going'}
                      >
                        I'm Going
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full" 
                        onClick={() => handleRSVP('maybe')}
                        disabled={userParticipation.status === 'maybe'}
                      >
                        Maybe
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full" 
                        onClick={handleLeave}
                      >
                        Leave Event
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button 
                        className="w-full" 
                        onClick={() => handleRSVP('going')}
                      >
                        I'm Going
                      </Button>
                      <Button 
                        variant="outline" 
                        className="w-full" 
                        onClick={() => handleRSVP('maybe')}
                      >
                        Maybe
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Participants
                </CardTitle>
              </CardHeader>
              <CardContent>
                {participantsLoading ? (
                  <Skeleton className="h-20 w-full" />
                ) : participants && participants.length > 0 ? (
                  <Tabs defaultValue="going" className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="going">Going ({goingCount})</TabsTrigger>
                      <TabsTrigger value="maybe">Maybe ({maybeCount})</TabsTrigger>
                    </TabsList>
                     <TabsContent value="going" className="space-y-2 mt-4">
                      {participants
                        .filter(p => p.status === 'going')
                        .map((participant) => (
                          <div key={participant.id} className="flex items-center justify-between p-2 rounded-lg border">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <span className="text-sm truncate">{participant.user_email}</span>
                              {event?.waiver_required && (
                                <div className="flex-shrink-0">
                                  {participant.waiver_signed ? (
                                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                                  ) : (
                                    <Circle className="w-4 h-4 text-muted-foreground" />
                                  )}
                                </div>
                              )}
                            </div>
                            {isEventCreator && participant.user_id !== user?.id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveParticipant(participant.user_id)}
                                className="ml-2"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                      {participants.filter(p => p.status === 'going').length === 0 && (
                        <p className="text-sm text-muted-foreground">No confirmed participants yet</p>
                      )}
                    </TabsContent>
                    <TabsContent value="maybe" className="space-y-2 mt-4">
                      {participants
                        .filter(p => p.status === 'maybe')
                        .map((participant) => (
                          <div key={participant.id} className="flex items-center justify-between p-2 rounded-lg border">
                            <span className="text-sm truncate">{participant.user_email}</span>
                            {isEventCreator && participant.user_id !== user?.id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveParticipant(participant.user_id)}
                                className="ml-2"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                      {participants.filter(p => p.status === 'maybe').length === 0 && (
                        <p className="text-sm text-muted-foreground">No maybe participants</p>
                      )}
                    </TabsContent>
                  </Tabs>
                ) : (
                  <p className="text-sm text-muted-foreground">No participants yet</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Comments Section */}
        <div className="mt-8">
          <EventComments eventId={eventId!} />
        </div>

        {/* Waiver Dialog */}
        {event?.waiver_required && event.waiver_text && (
          <WaiverDialog
            open={showWaiverDialog}
            onOpenChange={setShowWaiverDialog}
            waiverText={event.waiver_text}
            eventTitle={event.title}
            onAccept={handleWaiverAccept}
            isLoading={rsvpMutation.isPending}
          />
        )}
      </main>
    </div>
  );
};

export default EventDetail;
